/* RESPONSIBLE TEAM: team-channels */

import { tracked } from '@glimmer/tracking';

export enum SlaStateTargetType {
  FirstReplyTime = 'first_reply_time',
  NextReplyTime = 'next_reply_time',
  TimeToClose = 'time_to_close',
  TimeToResolve = 'time_to_resolve',
  SpeedOfAnswer = 'speed_of_answer',
}

export enum SlaStatus {
  Active = 'active',
  Hit = 'hit',
  Missed = 'missed',
  BreachFixed = 'breach_fixed',
  Paused = 'paused',
}

export interface ConversationSlaStateWireFormat {
  target_type: SlaStateTargetType;
  next_breach_at?: Date;
  paused_at?: Date;
  seconds_remaining_at_pause?: number;
  status?: SlaStatus;
  sla_name?: string;
}

const SIXTY_SECONDS = 60;

export default class ConversationSlaState {
  @tracked targetType: SlaStateTargetType;
  @tracked nextBreachAt?: Date;
  @tracked pausedAt?: Date;
  @tracked remainingSeconds?: number;
  @tracked status?: SlaStatus;
  @tracked slaName?: string;

  constructor(
    target_type: SlaStateTargetType,
    next_breach_at: Date | undefined,
    paused_at: Date | undefined,
    remaining_seconds?: number,
    status?: SlaStatus,
    sla_name?: string,
  ) {
    this.targetType = target_type;
    this.nextBreachAt = next_breach_at ? new Date(next_breach_at) : undefined;
    this.pausedAt = paused_at ? new Date(paused_at) : undefined;
    this.remainingSeconds = remaining_seconds;
    this.status = status;
    this.slaName = sla_name;
  }

  get isBreached(): boolean {
    return !!this.nextBreachAt && this.nextBreachAt.getTime() < Date.now();
  }

  get isPaused(): boolean {
    return this.status === SlaStatus.Paused;
  }

  get relativeTimeRemaining(): string {
    if (this.isPaused && this.remainingSeconds !== undefined) {
      return this.formatSeconds(Math.abs(this.remainingSeconds));
    }

    if (!this.nextBreachAt) {
      return '';
    }

    let diffMs = this.nextBreachAt.getTime() - Date.now();
    let diffSeconds = Math.round(Math.abs(diffMs) / 1000);
    return this.formatSeconds(diffSeconds);
  }

  get formattedBreachTime(): string {
    if (!this.nextBreachAt) {
      return '';
    }
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
      this.nextBreachAt,
    );
  }

  get formattedBreachDate(): string {
    if (!this.nextBreachAt) {
      return '';
    }
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(this.nextBreachAt);
  }

  private formatSeconds(totalSeconds: number): string {
    if (totalSeconds < SIXTY_SECONDS) {
      return `${totalSeconds}s`;
    }

    let minutes = Math.round(totalSeconds / 60);
    if (minutes < 60) {
      return `${minutes}m`;
    }

    let hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours}h`;
    }

    let days = Math.round(hours / 24);
    return `${days}d`;
  }

  get shortLabel() {
    if (this.nextBreachAt && this.nextBreachAt.getTime() < Date.now()) {
      return `inbox.conversation-details.slas.${this.targetType}.short.breached`;
    } else {
      return `inbox.conversation-details.slas.${this.targetType}.short.due`;
    }
  }

  get longLabel() {
    return `inbox.conversation-details.slas.${this.targetType}.long`;
  }

  static deserialize(json: ConversationSlaStateWireFormat): ConversationSlaState {
    return new ConversationSlaState(
      json.target_type,
      json.next_breach_at,
      json.paused_at,
      json.seconds_remaining_at_pause,
      json.status,
      json.sla_name,
    );
  }
}
